import { useCallback } from 'react';
import { message as antdMessage } from 'antd';
import { validateByFieldName } from '../utils/fieldFormatValidators';

/**
 * Installs a `fieldChanged` listener on the Apryse annotation manager that
 * validates and normalizes form-field input against common legal-document
 * formats (SSN, EIN, phone, ZIP, date).
 *
 * Behavior:
 *   - On valid-but-un-normalized input, quietly rewrites the field value to
 *     its canonical form (e.g. "123456789" -> "123-45-6789").
 *   - On invalid input, surfaces a hint via antd's global `message`
 *     (configurable via `onInvalidFormat`) and leaves the original value so
 *     the user can correct it.
 *
 * Returns a cleanup function that removes the listener.
 */
const useFieldFormatValidator = () => {
  const installFieldFormatValidators = useCallback((instance, options = {}) => {
    if (!instance?.Core) return () => {};
    const { annotationManager } = instance.Core;
    if (!annotationManager?.addEventListener) return () => {};

    const onInvalidFormat = options.onInvalidFormat
      || ((msg) => {
        try {
          antdMessage.warning({ content: msg, duration: 3 });
        } catch (_) { /* antd may not be mounted in tests */ }
      });

    // Guard against echo: when we programmatically re-set a field value, we
    // don't want to re-validate the reformatted value.
    const reformattingFields = new WeakSet();

    const handleFieldChanged = (field, newValue) => {
      if (!field || reformattingFields.has(field)) {
        reformattingFields.delete(field);
        return;
      }

      const fieldName = field.name || field.getName?.();
      const result = validateByFieldName(fieldName, newValue);

      if (!result.ok) {
        onInvalidFormat(result.message, { fieldName, value: newValue });
        return;
      }

      // If the canonical form differs from what the user typed, rewrite it.
      if (result.value !== newValue && typeof field.setValue === 'function') {
        try {
          reformattingFields.add(field);
          field.setValue(result.value);
        } catch (err) {
          reformattingFields.delete(field);
          console.warn('[field-format-validators] setValue failed:', err);
        }
      }
    };

    annotationManager.addEventListener('fieldChanged', handleFieldChanged);

    return () => {
      try {
        annotationManager.removeEventListener?.('fieldChanged', handleFieldChanged);
      } catch (_) { /* noop */ }
    };
  }, []);

  return { installFieldFormatValidators };
};

export default useFieldFormatValidator;
